import { Button, Icon, notification, Spinner, Title } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import { EDIT_SCRIPT_PATH } from "constants/app-paths";
import {
  useGetScriptDetailQuery,
  useGetViewScriptTimeStampQuery,
  useMarkScriptAsFavoriteMutation,
} from "features/script/api";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { setScriptsDetailData } from "store/scriptDetailSlice";
import theme from "utils/themeVars";
import ScriptLeftPanel from "../addScriptForm/scriptLeftPanel";
import styles from "../index.module.less";
import FeedbackModal from "./feedbackModal";
import ScriptDetailRightPanel from "./scriptDetailRightPanel";
import ScriptNotesSection from "./scriptNotesSection";
import VersionHistoryDropDown from "./versionHistoryDropDown";
import ShareButton from "components/custom/shareButton";

const ScriptDetails = () => {
  const params = useParams();
  const isMobile = useSelector((state) => state?.app?.isMobile);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryObj = Object.fromEntries([...searchParams]);
  // Community links use /scripts/:id while older script screens use
  // ?editorId=:id. Both values identify the same editor template.
  const editorTemplateId = queryObj?.editorId || params?.id;
  const dispatch = useDispatch();
  const [notesDrawer, setNotesDrawer] = useState(null);
  const navigate = useNavigate();
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  const details = useSelector((state) => state?.scriptDetail?.data);
  const user = useSelector((state) => state?.app?.user?.info);

  const [markAsFavorite, { isLoading: markingFavorite }] =
    useMarkScriptAsFavoriteMutation();

  useGetViewScriptTimeStampQuery(
    {
      scriptId: details?.editor_template_id,
      userId: user?._id,
    },
    {
      skip: !details?.editor_template_id || !params?.id || !user?._id,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: scriptDetails,
    isLoading: scriptDetailsLoading,
    error: detailError,
  } = useGetScriptDetailQuery(
    {
      editor_template_id: editorTemplateId,
      version: queryObj?.version,
    },
    { skip: !editorTemplateId }
  );

  useEffect(() => {
    if (scriptDetails && Object.keys(scriptDetails)?.length) {
      let temp = JSON.parse(
        JSON.stringify(scriptDetails?.assessment_and_plans)
      );
      let final = [];
      const index = temp.findIndex(
        (obj) => !!obj?.pinnedBy?.find((e) => e === user?._id)
      );
      if (index === -1 || index === 0) {
        final = temp;
      } else {
        final = [
          temp[index],
          ...temp.slice(0, index),
          ...temp.slice(index + 1),
        ];
      }
      dispatch(
        setScriptsDetailData({
          ...scriptDetails,
          scriptId: scriptDetails?.editor_template_id,
          editor_template_id: scriptDetails?.editor_template_id,
          assessment_and_plans: final,
        })
      );
    }
  }, [scriptDetails]);

  function createLeftSection() {
    const arr = [];
    arr.push({ id: "Presentation", title: "Presentation" });
    scriptDetails?.section_2?.forEach((element) => {
      arr.push({ id: element.title, title: element.title });
    });
    arr.push({ title: "Assessment and Plan", id: "Assessment and Plan" });
    arr.push({ title: "Questions", id: "Questions" });
    scriptDetails?.schemas?.length &&
      arr.push({ title: "Schemas", id: "Schemas" });
    scriptDetails?.podCast?.length &&
      arr.push({ title: "Podcasts", id: "Podcasts" });
    // arr.push({ title: "Page Details", id: "Page Details" });

    return arr;
  }

  const isFavorite = useCallback(() => {
    return scriptDetails?.favorites?.find((e) => e === user?._id);
  }, [scriptDetails, user?._id]);

  const handleClickFavorite = async () => {
    const res = await markAsFavorite({
      id: scriptDetails?.editor_template_id,
      userId: user?._id,
    });

    if (res?.error) {
      notification.error({ message: res?.error });
    } else {
      !isFavorite()
        ? notification.success({
            message: "Script has been added to favorites successfully!",
          })
        : notification.success({
            message: "Script has been removed favorites successfully!",
          });
    }
  };

  const dropDownItems = [
    {
      label: "Edit",
      icon: "FiEdit2",
      onClick: () => {
        navigate(
          `${EDIT_SCRIPT_PATH.replace(":name", details?.page_name)}?editorId=${
            details?.editor_template_id
          }&version=${details?.version}`
        );
      },
    },
    {
      label: "Add Feedback",
      icon: "BiComment",
      onClick: () => {
        setFeedbackVisible(true);
      },
    },
  ];

  return (
    <PageLayout
      showSubMenu={false}
      backIcon={true}
      className="p-4"
      showDropDown={true}
      dropDownItems={dropDownItems}
      label={
        !scriptDetailsLoading ? (
          <div className={styles.pageLabel}>
            <Title level={2}>{details?.page_name}</Title>
            <Icon
              icon={isFavorite() ? "FaBookmark" : "LuBookmark"}
              size="18px"
              color="#FF9500"
              className="pointer"
              onClick={handleClickFavorite}
              loading={markingFavorite}
            />
          </div>
        ) : (
          ""
        )
      }
      renderCustomActions={() => (
        <>
          <VersionHistoryDropDown />
          {details?.editor_template_id && (
            <ShareButton
              objectType="script"
              objectId={details?.editor_template_id}
              resource={{ title: details?.page_name, specialty: details?.primarySpeciality || details?.speciality?.[0], thumbnail: details?.media?.[0]?.url || details?.media?.[0]?.imageURL }}
              type="light"
              text="Recommend"
            />
          )}
          <Button
            icon={"MdEditNote"}
            iconProps={{ size: "16px", color: theme["primary-color"] }}
            shape="circle"
            style={{ border: "1px solid #c5c6d0" }}
            content={"Add/Edit Notes"}
            onClick={() => setNotesDrawer((prev) => !prev)}
          />
        </>
      )}
    >
      <ScriptNotesSection
        open={notesDrawer}
        setOpen={() => setNotesDrawer(false)}
        editorTemplateId={details?.editor_template_id}
        notes={details?.user_note?.note}
      />

      {scriptDetailsLoading ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : detailError ? (
        "Error"
      ) : (
        <div className={styles.pageContainer}>
          {!isMobile && <ScriptLeftPanel explanations={createLeftSection()} />}
          <ScriptDetailRightPanel
            data={details}
            isMobile={isMobile}
            queryObj={queryObj}
          />
          <FeedbackModal
            feedbackVisible={feedbackVisible}
            setFeedbackVisible={setFeedbackVisible}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default ScriptDetails;
